import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Card, CardBody, Col, Container, Row, Modal, ModalBody } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { MaterialReactTable } from "material-react-table";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../Common/BreadCrumb";
import { Loader } from "../Utils/Loader";
import CommonModal from "../Utils/CommonModal";
import UserDetails from "./UserDetail";
import { setEditSuccessMessage, setEmailUpdateSuccessMessage, setSuccessMessage } from "../../slices/toastMessage/action";
import { UserCustomButtons } from "./UserCustomButton";
import { UserRowOptions } from "./UserRowOption";
import { extractedDataForUser, userColoumns } from "./Constant/UserManagementColoumn";
import { getAllUser } from "../../slices/UserManagement/thunk";
import { deleteUser } from "../../slices/UserManagement/DeleteUser/thunk";
import { useColumnSettings } from '../Utils/useColumnSettings';
import { getView } from '../../slices/View/thunk';
import { MenuItem } from "@mui/material";
import { resetDocumentViewState } from "../../slices/customer/documentview/reducer";
import { fetchGetUserNameSucess } from "../../slices/UserManagement/GetUserByName/reducer";
import { resetPassword } from "../../slices/ResetPassword/thunk";
import { deactivateUser } from "../../slices/UserManagement/Deactivate/thunk";
import { resendLink } from "../../slices/ResendLink/thunk";
import { activateUser } from "../../slices/UserManagement/Activate/thunk";
import CreateEditUser from "./CreateEditUser";
import { hasPermission } from "../Utils/UtilityFunctions";
import { fetchAllUserRoles } from "../../slices/UserRoleManagement/thunk"
import { CustomRole } from '../UserRoleManagement/Constant/constants';
import AddEditUserModal from "./CreateEditUserModal";

export default function UserManagementListing() {
    const [loading, setLoading] = useState()
    const [messageForPopUp, setMessageForPopUp] = useState('')
    const [modal, setModal] = useState(false);
    const [activateModal, setActivateModal] = useState(false);
    const [deactivateModal, setDeactivateModal] = useState(false);
    const [userName, setUserName] = useState()
    const [userId, setUserId] = useState()
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [data, setData] = useState([])
    const [userDetailsModal, setUserDetailsModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const location = useLocation()
    const isUserPath = location.pathname === "/user";
    const { domain } = useSelector((state) => state.permissions.permissions)
    const userRoles = useSelector((state) => state.GetAllUserRoles.getAllUserRoles)
    const customRoles = userRoles?.length > 0 ? userRoles?.filter(user => user?.roletype.toLowerCase() === CustomRole) : [];

    // Add state to track if toast has been shown to prevent duplicates
    const [toastShown, setToastShown] = useState({
        success: false,
        edit: false,
        email: false
    });

    const roleName = localStorage.getItem("roleName")
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const columns = userColoumns()
    const operatorId = localStorage.getItem('operatorId');
    const user = useSelector((state) => state?.GetAllUser?.getAllUser)
    const rawPermissions = useSelector(
        (state) => state.permissions.permissionsMap
    );

    const permissionsMap = new Map(
        rawPermissions.map(([key, value]) => [key, new Set(value)])
    );
    const userPermission = hasPermission(permissionsMap, "administration", "userManagement")
    const viewPermission = hasPermission(permissionsMap, "miscellaneous", "view")
    const showSuccessMessage = useSelector((state) => state.SuccessToast.showSuccessMessage)
    const showEditSuccessMessage = useSelector((state) => state.SuccessToast.showEditSuccessMessage);
    const showEmailUpdateSuccessMessage = useSelector((state) => state.SuccessToast.showEmailUpdateSuccessMessage)

    const updatedData = useMemo(() => {
        const listingData = extractedDataForUser(user);
        return listingData.map(item => ({
            ...item,
            status: item.status === 'active' ? 'Active' : (item.status === 'inactive' ? 'Inactive' : (item.status === 'unverified' ? 'Unverified' : item.status === 'unregistered' ? 'Unregistered' : '-'))
        }));
    }, [user]);

    // Enhanced toggle function for UserDetails modal with proper cleanup
    const toggleUserDetailsModal = useCallback(() => {
        const newModalState = !userDetailsModal;
        setUserDetailsModal(newModalState);

        if (!newModalState) {
            // Modal is being closed - reset all form states
            setSelectedUser(null);
            setIsEditMode(false);
            // Reset toast flags when modal closes
            setToastShown({ success: false, edit: false, email: false });
        }

        // Manage body scroll
        document.body.style.overflow = newModalState ? 'hidden' : 'auto';
    }, [userDetailsModal]);

    // Function to handle successful operations and close modal
    const handleOperationSuccess = useCallback((type) => {
        // Close modal first
        setUserDetailsModal(false);
        setSelectedUser(null);
        setIsEditMode(false);
        document.body.style.overflow = 'auto';

        // Refresh user list
        dispatch(getAllUser(setLoading, operatorId));

        // Reset toast flags after a short delay to allow for re-showing if needed
        setTimeout(() => {
            setToastShown({ success: false, edit: false, email: false });
        }, 1000);
    }, [dispatch, operatorId]);

    // Function to open modal for adding user
    const handleAddUser = useCallback(() => {
        setIsEditMode(false);
        setSelectedUser(null);
        setToastShown({ success: false, edit: false, email: false });
        setUserDetailsModal(true);
        document.body.style.overflow = 'hidden';
    }, []);

    // Function to open modal for editing user
    const handleEditUser = useCallback((userData) => {
        setIsEditMode(true);
        setSelectedUser(userData);
        setToastShown({ success: false, edit: false, email: false });
        setUserDetailsModal(true);
        document.body.style.overflow = 'hidden';
    }, []);

    const toggleModal = (id) => {
        setUserName(id)
        setModal(!modal);
        document.body.style.overflow = 'auto'
    };

    const activateToggleModal = (id) => {
        setUserId(id)
        setActivateModal(!activateModal);
        document.body.style.overflow = 'auto'
    };

    const deactivateToggleModal = (id) => {
        setUserId(id)
        setDeactivateModal(!deactivateModal);
        document.body.style.overflow = 'auto'
    };

    const handleCancel = () => {
        setGlobalFilter('');
        setColumnFilters([]);
        setData(user);
    };

    const handleGlobalFilterChange = (filter) => {
        setGlobalFilter(filter);
        if (!filter) {
            handleCancel();
        }
    };

    useEffect(() => {
        dispatch(getAllUser(setLoading, operatorId));
        dispatch(getView(setLoading))
    }, [dispatch]);

    useEffect(() => {
        const filteredData = updatedData.filter(row => {
            const globalFilterLower = globalFilter.toLowerCase();
            const columnFilterChecks = columnFilters.map(filter => {
                const filterValue = filter?.value?.toLowerCase();
                const cellValue = row[filter.id]?.toString().toLowerCase();
                if (filter.id === 'status') {
                    return cellValue === filterValue;
                }
                return cellValue?.includes(filterValue);
            });

            const userFieldMatches = (
                (row?.userId && typeof row?.userId === 'string' && row?.userId.toLowerCase().includes(globalFilterLower)) ||
                (row?.userName && typeof row?.userName === 'string' && row?.userName.toLowerCase().includes(globalFilterLower)) ||
                (row?.fullName && typeof row?.fullName === 'string' && row?.fullName.toLowerCase().includes(globalFilterLower)) ||
                (row?.role && typeof row?.role === 'string' && row?.role.toLowerCase().includes(globalFilterLower)) ||
                (row?.email && typeof row?.email === "string" && row?.email?.toLowerCase().includes(globalFilterLower)) ||
                (row?.phone && typeof row?.phone === "string" && row?.phone?.toLowerCase().includes(globalFilterLower)) ||
                (row?.status && row?.status?.toLowerCase() === globalFilterLower)
            );
            const matchesGlobalFilter = userFieldMatches;
            return matchesGlobalFilter && columnFilterChecks.every(Boolean);
        });

        setData(filteredData);
    }, [globalFilter, columnFilters, updatedData]);

    const {
        settingsModalOpen,
        viewColumns,
        toggleSettingsModal,
        SettingsSidebar,
        CustomViewModal
    } = useColumnSettings('user', columns);

    // FIXED: Enhanced useEffect to handle success messages with proper flags
    useEffect(() => {
        if (showSuccessMessage && !toastShown.success) {
            toast.success(`An email link for setting the password has been sent.`);
            dispatch(setSuccessMessage(false));
            setToastShown(prev => ({ ...prev, success: true }));
        }
    }, [showSuccessMessage, toastShown.success, dispatch]);

    useEffect(() => {
        if (showEditSuccessMessage && !toastShown.edit) {
            toast.success(`User Updated Successfully`);
            dispatch(setEditSuccessMessage(false));
            setToastShown(prev => ({ ...prev, edit: true }));
        }
    }, [showEditSuccessMessage, toastShown.edit, dispatch]);

    useEffect(() => {
        if (showEmailUpdateSuccessMessage && !toastShown.email) {
            toast.success(`Verification link has been sent to your updated email`);
            dispatch(setEmailUpdateSuccessMessage(false));
            setToastShown(prev => ({ ...prev, email: true }));
        }
    }, [showEmailUpdateSuccessMessage, toastShown.email, dispatch]);

    const containsNull = viewColumns.some(value => value === undefined);
    const result = !containsNull;
    const userSummary = {
        numberOfUsers: data.length,
        activeUsers: data.filter(d => (d.status || "").toLowerCase() === "active").length,
        inactiveUsers: data.filter(d => (d.status || "").toLowerCase() === "inactive").length,
    };
    userSummary.otherUsers = userSummary?.numberOfUsers - userSummary?.activeUsers - userSummary?.inactiveUsers;

    useEffect(() => {
        if (domain) {
            dispatch(fetchAllUserRoles(setLoading, domain));
        }
    }, [dispatch, domain]);

    return (
        <React.Fragment>
            <div id='csms' className='page-content bg-white'>
                <Container fluid>
                    <BreadCrumb title="Users" pageTitle="User" />
                    <ToastContainer position='top-center' />
                    <Row>
                        <Col lg={12}>
                            <CardBody>
                                <Row className="mb-3 mt-1 table-responsive">
                                    {loading && (<Loader />)}
                                    {user ?
                                        <MaterialReactTable
                                            icons={{ DragHandleIcon: () => <i className="ri-drag-move-fill" />, }}
                                            displayColumnDefOptions={{
                                                'mrt-row-actions': {
                                                    header: '',
                                                    enableHiding: true,
                                                    size: 53,
                                                    grow: true
                                                },
                                            }}
                                            muiTableContainerProps={{
                                                sx: {
                                                    border: '1px solid #e9ebec',
                                                }
                                            }}
                                            defaultColumn={{
                                                minSize: 10,
                                                maxSize: 9001,
                                                muiTableBodyCellProps: {
                                                    sx: {
                                                        fontFamily: "Inter,sans-serif"
                                                    }
                                                },
                                                muiTableHeadCellProps: {
                                                    sx: {
                                                        fontFamily: "Inter,sans-serif"
                                                    }
                                                }
                                            }}
                                            layoutMode='grid'
                                            renderTopToolbarCustomActions={({ }) => (

                                                <UserCustomButtons
                                                    toggleSettingsModal={toggleSettingsModal}
                                                    Policies={user}
                                                    navigate={navigate}
                                                    userPermission={userPermission}
                                                    handleAddUser={handleAddUser}
                                                    userSummary={userSummary}
                                                    viewPermission={viewPermission}
                                                />


                                            )}
                                            columns={result ? columns.filter((column) => column.accessorKey ? viewColumns?.includes(column.accessorKey) : viewColumns?.includes(column.header)) : columns.filter((column) => viewColumns?.includes(column.accessorKey))}
                                            data={data}
                                            enableRowActions
                                            enableColumnActions={true}
                                            onColumnFiltersChange={setColumnFilters}
                                            onGlobalFilterChange={handleGlobalFilterChange}
                                            state={{ globalFilter, columnFilters }}
                                            enableDensityToggle={false}
                                            renderRowActionMenuItems={({ closeMenu, row }) => [
                                                userPermission && userPermission?.has('view') ? <MenuItem className='row-options' data-testid="update" key="view" onClick={() => { navigate(`/view-user/${row.original.userId}`); dispatch(resetDocumentViewState()); dispatch(fetchGetUserNameSucess([])) }} >
                                                    <i className='ri-account-circle-line me-2' /> View </MenuItem> : null,
                                                userPermission && userPermission?.has('resend') && row.original.status === 'Unregistered' ?
                                                    <MenuItem className='row-options' data-testid="resend" key="resend" onClick={() => { dispatch(resendLink(setLoading, row.original.userId)) }}>
                                                        <i className='ri-send-plane-2-line me-2' />Resend Registration Link </MenuItem> : null,
                                                ((roleName === "systemUser" && row.original.role !== "systemUser") ||
                                                    (roleName === "providerOperationUser" && (row.original.role !== "systemUser" || row.original.role !== "providerOperationUser")) ||
                                                    (roleName === "telcoAdmin" && (row.original.role === "telcoOperationUser" || row.original.role === "telcoBusinessUser")) ||
                                                    (roleName === "telcoOperationUser" && (row.original.role === "telcoBusinessUser")) ||
                                                    (roleName === "operatorAdmin" && (row.original.role === "operatorOperationUser" || row.original.role === "operatorBusinessUser")) ||
                                                    (roleName === "operatorOperationUser" && (row.original.role === "operatorBusinessUser")) ||
                                                    (roleName === "genericAdmin" && (row.original.role === "genericOperationUser" || row.original.role === "genericBusinessUser")) ||
                                                    (roleName === "genericOperationUser" && (row.original.role === "genericBusinessUser")) ||
                                                    (roleName === "ISPOperatorAdmin" && (row.original.role === "ISPOperationUser" || row.original.role === "ISPBusinessUser")) ||
                                                    (roleName === "ISPOperationUser" && (row.original.role === "ISPBusinessUser")) || 
                                                    (roleName === "SGCExecutiveUser" || roleName === "SGCPowerUser" || roleName === "SGCOperationsUser")                                                
                                                ) ?
                                                    <MenuItem className='row-options' data-testid="reset" key="reset" onClick={() => { dispatch(resetPassword(row.original.userId, setLoading)) }} >
                                                        <i className='ri-account-circle-line me-2' /> Reset password </MenuItem> : null,
                                                // Add edit option
                                                userPermission && userPermission?.has('edit') ?
                                                    <MenuItem className='row-options' data-testid="edit" key="edit" onClick={() => { handleEditUser(row.original); closeMenu(); }}>
                                                        <i className='ri-edit-line me-2' /> Edit </MenuItem> : null,
                                                // Other menu items...
                                                ((userPermission && userPermission?.has('deactivate')) && (row?.original?.status === 'Active' || row?.original?.status === 'active') &&
                                                    ((roleName !== "Customer" && (row.original.role !== "telcoAdmin" && row.original.role !== "operatorAdmin" && row.original.role !== "genericAdmin" && row.original.role !== "ISPOperatorAdmin" && row.original.role !== "systemUser" && row.original.role !== "ISPPartnerAdmin")) ||
                                                        (roleName === "providerOperationUser" && (row.original.role !== "telcoAdmin" && row.original.role !== "operatorAdmin" && row.original.role !== "genericAdmin" && row.original.role !== "ISPOperatorAdmin" && row.original.role !== "systemUser" && row.original.role !== "ISPPartnerAdmin")) ||
                                                        (roleName === "telcoAdmin" && (row.original.role === "telcoOperationUser" || row.original.role === "telcoBusinessUser")) ||
                                                        (roleName === "telcoOperationUser" && (row.original.role === "telcoBusinessUser")) ||
                                                        (roleName === "operatorAdmin" && (row.original.role === "operatorOperationUser" || row.original.role === "operatorBusinessUser")) ||
                                                        (roleName === "operatorOperationUser" && (row.original.role === "operatorBusinessUser")) ||
                                                        (roleName === "genericAdmin" && (row.original.role === "genericOperationUser" || row.original.role === "genericBusinessUser")) ||
                                                        (roleName === "genericOperationUser" && (row.original.role === "genericBusinessUser")) ||
                                                        (roleName === "ISPOperatorAdmin" && (row.original.role === "ISPOperationUser" || row.original.role === "ISPBusinessUser")) ||
                                                        (roleName === "ISPOperationUser" && (row.original.role === "ISPBusinessUser")) ||
                                                        (roleName === "SGCExecutiveUser" || roleName === "SGCPowerUser" || roleName === "SGCOperationsUser")                                                                          )) ?
                                                    <MenuItem className='row-options' data-testid="deactivate" key="deactivate" onClick={() => {
                                                        setMessageForPopUp(`Are you sure you want to deactivate "${row.original.userName}"?`), deactivateToggleModal(row), closeMenu(), setUserId(row.original.userId)
                                                    }}><i className='ri-close-circle-line me-2' /> Deactivate </MenuItem> : null,
                                                ((userPermission && userPermission?.has('activate')) && (row.original.status === 'Inactive' || row.original.status === 'inactive') &&
                                                    ((roleName === "systemUser" && (row.original.role !== "telcoAdmin" && row.original.role !== "operatorAdmin" && row.original.role !== "genericAdmin" && row.original.role !== "ISPOperatorAdmin" && row.original.role !== "systemUser" && row.original.role !== "ISPPartnerAdmin")) ||
                                                        (roleName === "providerOperationUser" && (row.original.role !== "telcoAdmin" && row.original.role !== "operatorAdmin" && row.original.role !== "genericAdmin" && row.original.role !== "ISPOperatorAdmin" && row.original.role !== "systemUser" && row.original.role !== "ISPPartnerAdmin")) ||
                                                        (roleName === "telcoAdmin" && (row.original.role === "telcoOperationUser" || row.original.role === "telcoBusinessUser")) ||
                                                        (roleName === "telcoOperationUser" && (row.original.role === "telcoBusinessUser")) ||
                                                        (roleName === "operatorAdmin" && (row.original.role === "operatorOperationUser" || row.original.role === "operatorBusinessUser")) ||
                                                        (roleName === "operatorOperationUser" && (row.original.role === "operatorBusinessUser")) ||
                                                        (roleName === "genericAdmin" && (row.original.role === "genericOperationUser" || row.original.role === "genericBusinessUser")) ||
                                                        (roleName === "genericOperationUser" && (row.original.role === "genericBusinessUser")) ||
                                                        (roleName === "ISPOperatorAdmin" && (row.original.role === "ISPOperationUser" || row.original.role === "ISPBusinessUser")) ||
                                                        (roleName === "ISPOperationUser" && (row.original.role === "ISPBusinessUser")) ||
                                                        (roleName === "SGCExecutiveUser" || roleName === "SGCPowerUser" || roleName === "SGCOperationsUser")                                                                          )) ?
                                                    <MenuItem className='row-options' data-testid="activate" key="activate" onClick={() => {
                                                        setMessageForPopUp(`Are you sure you want to Activate "${row.original.userName}"?`), activateToggleModal(row), closeMenu(), setUserId(row.original.userId)
                                                    }}><i className='ri-shut-down-line me-2' /> Activate </MenuItem> : null,
                                                (userPermission && userPermission?.has('delete'))
                                                    ? <MenuItem className='row-options' key="delete" onClick={() => { setMessageForPopUp(`Are you sure you want to delete "${row.original.userName}"?`), toggleModal(row), closeMenu(), setUserName(row.original.userId) }}>
                                                        <i className="ri ri-delete-bin-5-line me-2" />{" "}
                                                        Delete
                                                    </MenuItem> : null,
                                            ]}
                                        /> : null}
                                </Row>
                            </CardBody>
                        </Col>
                        {CustomViewModal()}
                    </Row>
                </Container>
            </div>

            {/* Enhanced Modal */}
            <AddEditUserModal
                isOpen={userDetailsModal}
                toggle={toggleUserDetailsModal}
                isEditMode={isEditMode}
                selectedUser={selectedUser}
                domain={domain}
                customRoles={customRoles}
                onSuccess={handleOperationSuccess}
                isUserPath={isUserPath}
                operatorUser={false}
            />

            <div>
                <CommonModal modalHeader="Delete User" open={modal} toggle={toggleModal} buttonText={"Yes, Delete It"} closeButtonText="Close" messageForPopUp={messageForPopUp} modalAction={() => {
                    dispatch(deleteUser(userName, setLoading, operatorId,location))
                    toggleModal()
                }} />
                <CommonModal open={activateModal} toggle={activateToggleModal} buttonText={"Activate"} closeButtonText="Close" messageForPopUp={messageForPopUp} modalAction={() => {
                    dispatch(activateUser(userId, setLoading, location))
                    activateToggleModal()
                }} />
                <CommonModal modalHeader="Deactivate User" open={deactivateModal} toggle={deactivateToggleModal} buttonText={"Deactivate"} closeButtonText="Close" messageForPopUp={messageForPopUp} modalAction={() => {
                    dispatch(deactivateUser(userId, setLoading, location))
                    deactivateToggleModal()
                }} />
            </div>
        </React.Fragment>
    )
}